import {connect} from 'react-redux';

import {onlyItem} from '../../../../../libs/shared/core/src/helpers/utilities/arrayHelpers';
import {ConversationSortPropertiesEnum} from '../../../../../libs/shared/core/src/models/api/conversationCommonTypes';
import {getHasSortP2BySlaTimerFF} from '../../../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {makeMapDispatchToProps, makeMapStateToProps} from '../../../../../srcCommon/helpers/reselectHelpers';
import {CellsSortDirectionsEnum} from '../../../../models/api/sortOptionModel';
import {InboxesFiltersEnum} from '../../../../models/url/models/inboxesUrlModel';
import {updateSortOptionAction} from '../../../../store/data/localPreferences/localPreferencesActions';
import {getIsUserVisitor} from '../../../../store/data/teammates/teammatesSelectors';
import {CellsPanelPageState} from '../../../../store/panels/cells/cellsPanelState';
import {
  sortOptionFromVisualSortOption,
  VisualSortOptionsEnum,
} from '../../../../store/panels/cells/helpers/cellsPanelSortHelpers';
import {getCellsSearchResultsCounts} from '../../../../store/panels/cells/search/cellsPanelSearchSelectors';
import {
  hasSearchInInboxesParameters,
  InboxesParameters,
  isInboxesConversationsParameters,
} from '../../../../store/panels/inboxes/parameters/inboxesParameters';
import {
  CellsSortModesEnum,
  clearInboxesParametersSelection,
  findParametersCellsSortMode,
} from '../../../../store/panels/inboxes/parameters/inboxesParametersHelpers';
import {buildParametersPath} from '../../../../store/panels/inboxes/parameters/inboxesParametersPathHelpers';
import {buildParametersSortKey} from '../../../../store/panels/inboxes/parameters/inboxesParametersSelectors';
import {navigateToLinkAction} from '../../../../store/stateless/navigationActions';
import {CellsSortOptions} from './cellsSortOptions';

/*
 * Props.
 */

interface CellsSortOptionsContainerProps {
  parameters: InboxesParameters;
  page: CellsPanelPageState;
  inboxSupportsRefinements: boolean;
}

export interface VisualSortOptionsProps {
  sortOptions: ReadonlyArray<VisualSortOptionsEnum>;
  selectedSortOption?: VisualSortOptionsEnum;
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps()(() => ({
  cellsSearchResultsCounts: getCellsSearchResultsCounts,
  isUserVisitor: getIsUserVisitor,
  hasSortP2BySlaTimerFF: getHasSortP2BySlaTimerFF,
}));

const mapDispatchToProps = makeMapDispatchToProps()(() => ({
  navigateToLink: navigateToLinkAction,
  updateSortOption: updateSortOptionAction,
}));

export const CellsSortOptionsContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
  (stateProps, dispatchProps, ownProps: CellsSortOptionsContainerProps) => {
    const {cellsSearchResultsCounts, isUserVisitor, hasSortP2BySlaTimerFF} = stateProps;
    const {page, parameters, inboxSupportsRefinements} = ownProps;

    const propsSortOptions = findSortOptionFromProps(parameters, page, hasSortP2BySlaTimerFF);

    const resultsCount = hasSearchInInboxesParameters(parameters)
      ? cellsSearchResultsCounts[parameters.search.location]
      : undefined;

    const sortOptions = {
      ...propsSortOptions,
      isUserVisitor,
      resultsCount,
      parameters,
      inboxSupportsRefinements,
      onSortOptionSelect: (visualSortOption: VisualSortOptionsEnum) => {
        const sortOptionValue = sortOptionFromVisualSortOption(parameters, visualSortOption);
        const sortOptionKey = buildParametersSortKey(parameters);

        // Update the local sort option for these parameters.
        dispatchProps.updateSortOption(sortOptionKey, sortOptionValue);

        // Otherwise, simply clear the selection
        return dispatchProps.navigateToLink(buildParametersPath(clearInboxesParametersSelection(parameters)));
      },
    };

    return sortOptions;
  },
)(CellsSortOptions);

/*
 * Helpers.
 */

export function findSortOptionFromProps(
  parameters: InboxesParameters,
  page: CellsPanelPageState,
  hasSortP2BySlaTimerFF: boolean,
): VisualSortOptionsProps {
  // If no page could be found, use the route parameters.
  const routeOptions = findSortOptions(parameters, hasSortP2BySlaTimerFF);
  const activeParameters = page && page.parameters;
  if (!activeParameters) {
    return routeOptions;
  }

  // Otherwise, compare the sort options.
  // We only use the route parameters if the options match.
  const activeOptions = findSortOptions(activeParameters, hasSortP2BySlaTimerFF);
  if (activeOptions.sortOptions === routeOptions.sortOptions) {
    return routeOptions;
  }

  // Otherwise, use the active sort options.
  return activeOptions;
}

function getLimitedSortOptions() {
  return [VisualSortOptionsEnum.NEWEST, VisualSortOptionsEnum.OLDEST];
}

function getRegularSortOptions(hasSortBySla: boolean) {
  if (hasSortBySla) {
    return [
      ...getLimitedSortOptions(),
      VisualSortOptionsEnum.HIGHEST_PRIORITY,
      VisualSortOptionsEnum.NEWEST_UNREPLIED,
    ];
  }

  return [
    ...getLimitedSortOptions(),
    VisualSortOptionsEnum.NEWEST_UNREPLIED,
    VisualSortOptionsEnum.OLDEST_UNREPLIED,
  ];
}

function getSnoozedSortOptions(hasSortBySla: boolean) {
  return [...getRegularSortOptions(hasSortBySla), VisualSortOptionsEnum.DUE_DATE];
}

function getSearchSortOptions() {
  return [
    ...getLimitedSortOptions(),
    VisualSortOptionsEnum.NEWEST_UNREPLIED,
    VisualSortOptionsEnum.OLDEST_UNREPLIED,
  ];
}

function findSortOptions(
  parameters: InboxesParameters,
  hasSortP2BySlaTimerFF: boolean,
): VisualSortOptionsProps {
  // Find the selected sort option, if any.
  const selectedSortOption = findSelectedSortOption(parameters);

  // Use the limited options if sortMode is 'MESSAGE'.
  if (findParametersCellsSortMode(parameters) === CellsSortModesEnum.MESSAGE) {
    return {
      sortOptions: getLimitedSortOptions(),
      selectedSortOption,
    };
  }

  // Use the limited options if sortMode is 'ACTIVITY'.
  if (findParametersCellsSortMode(parameters) === CellsSortModesEnum.ACTIVITY) {
    return {
      sortOptions: [VisualSortOptionsEnum.NEWEST],
      selectedSortOption,
    };
  }

  const isInSearchMode = isInboxesConversationsParameters(parameters) && parameters.search !== undefined;
  if (isInSearchMode) {
    return {
      sortOptions: getSearchSortOptions(),
      selectedSortOption,
    };
  }

  // If we're filtering snoozed conversations, add the 'DUE_DATE' sort option.
  if (
    isInboxesConversationsParameters(parameters) &&
    onlyItem(parameters.filters) === InboxesFiltersEnum.SNOOZED
  ) {
    return {
      sortOptions: getSnoozedSortOptions(hasSortP2BySlaTimerFF),
      selectedSortOption,
    };
  }

  return {
    sortOptions: getRegularSortOptions(hasSortP2BySlaTimerFF),
    selectedSortOption,
  };
}

function findSelectedSortOption(parameters: InboxesParameters) {
  const {sortDirection} = parameters;
  const sortMode = findParametersCellsSortMode(parameters);

  const isAscending = sortDirection === CellsSortDirectionsEnum.ASCENDING;
  const conversationsSortProperty = sortMode === CellsSortModesEnum.ORDER && parameters.sortProperty;
  const isWaitingSinceProperty = conversationsSortProperty === ConversationSortPropertiesEnum.WAITING_SINCE;
  const expiresAtProperty = conversationsSortProperty === ConversationSortPropertiesEnum.EXPIRES_AT;

  // Priority (SLA).
  if (conversationsSortProperty === ConversationSortPropertiesEnum.PRIORITY) {
    return isAscending ? VisualSortOptionsEnum.HIGHEST_PRIORITY : VisualSortOptionsEnum.NEWEST_UNREPLIED;
  }

  // Oldest unreplied.
  if (isWaitingSinceProperty) {
    return isAscending ? VisualSortOptionsEnum.OLDEST_UNREPLIED : VisualSortOptionsEnum.NEWEST_UNREPLIED;
  }

  // Expires at.
  if (expiresAtProperty) {
    return isAscending ? VisualSortOptionsEnum.DUE_DATE : undefined;
  }

  // Oldest.
  if (isAscending) {
    return VisualSortOptionsEnum.OLDEST;
  }

  // Newest.
  return VisualSortOptionsEnum.NEWEST;
}
